/**
 * CycleManagement — Admin page for managing evaluation cycles.
 * Cycles belong to batches and can have templates assigned.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Loader2, Plus, Pencil, Archive } from "lucide-react";
import { BACKEND_URL } from "../lib/config";

export default function CycleManagement() {
  const [batches, setBatches] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCycle, setEditingCycle] = useState(null);
  const [selectedBatchId, setSelectedBatchId] = useState("");
  const [form, setForm] = useState({
    name: "",
    batch_id: "",
    template_id: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([
      axios.get(`${BACKEND_URL}/api/batches`),
      axios.get(`${BACKEND_URL}/api/templates`),
    ])
      .then(([b, t]) => {
        setBatches(b.data);
        setTemplates(t.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const fetchCycles = async (batchId) => {
    if (!batchId) {
      setCycles([]);
      return;
    }
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/batches/${batchId}/cycles`
      );
      setCycles(res.data);
    } catch {
      setCycles([]);
    }
  };

  const handleBatchChange = (batchId) => {
    setSelectedBatchId(batchId);
    fetchCycles(batchId);
  };

  const openCreateDialog = () => {
    setEditingCycle(null);
    if (selectedBatchId) {
      setForm({ name: "", batch_id: selectedBatchId, template_id: "" });
    } else {
      setForm({ name: "", batch_id: "", template_id: "" });
    }
    setError("");
    setDialogOpen(true);
  };

  const openEditDialog = (cycle) => {
    setEditingCycle(cycle);
    setForm({
      name: cycle.name,
      batch_id: cycle.batch_id,
      template_id: cycle.template_id || "__none__",
    });
    setError("");
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.batch_id) {
      setError("Name and batch are required");
      return;
    }
    const cleanTemplateId = form.template_id === "__none__" ? null : (form.template_id || null);
    setSaving(true);
    setError("");
    try {
      if (editingCycle) {
        await axios.put(
          `${BACKEND_URL}/api/admin/cycles/${editingCycle.id}`,
          {
            name: form.name,
            template_id: cleanTemplateId,
          }
        );
      } else {
        await axios.post(
          `${BACKEND_URL}/api/admin/batches/${form.batch_id}/cycles`,
          {
            name: form.name,
            batch_id: form.batch_id,
            template_id: cleanTemplateId,
          }
        );
      }
      setDialogOpen(false);
      fetchCycles(selectedBatchId || form.batch_id);
      if (!selectedBatchId) setSelectedBatchId(form.batch_id);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to save cycle");
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (cycleId) => {
    if (!window.confirm("Archive this cycle?")) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/cycles/${cycleId}`);
      fetchCycles(selectedBatchId);
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to archive");
    }
  };

  const getBatchName = (batchId) =>
    batches.find((b) => b.id === batchId)?.name || batchId;
  const getTemplateName = (templateId) =>
    templates.find((t) => t.id === templateId)?.name || "None";

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="animate-spin" size={24} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Evaluation Cycles</h1>
            <p className="text-sm text-muted-foreground">
              Manage evaluation cycles for each batch
            </p>
          </div>
        </div>

        {/* Batch Filter */}
        <Card className="bg-[#111827] border-border/60">
          <CardContent className="p-4">
            <div className="flex items-end gap-4">
              <div className="flex-1">
                <Label>Select Batch</Label>
                <Select
                  value={selectedBatchId}
                  onValueChange={handleBatchChange}
                >
                  <SelectTrigger className="mt-1 bg-secondary/50 border-border/60">
                    <SelectValue placeholder="Choose a batch to view its cycles" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111827] border-border/60">
                    {batches.map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={openCreateDialog}
                className="bg-[#2BB5D8] hover:bg-[#25a3c4] text-[#0D1117] font-semibold"
              >
                <Plus size={16} className="mr-1" /> Add Cycle
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Cycles List */}
        {selectedBatchId && (
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Cycles for {getBatchName(selectedBatchId)}
            </h2>
            {cycles.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">
                No cycles yet. Click "Add Cycle" to create one.
              </p>
            ) : (
              cycles.map((cycle) => (
                <Card
                  key={cycle.id}
                  className={`bg-[#111827] border-border/60 ${
                    !cycle.is_active ? "opacity-50" : ""
                  }`}
                >
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{cycle.name}</h3>
                        {cycle.is_active ? (
                          <Badge className="bg-green-600/20 text-green-400 text-[10px]">
                            Active
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-600/20 text-amber-400 text-[10px]">
                            Archived
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Template: {getTemplateName(cycle.template_id)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(cycle)}
                      >
                        <Pencil size={14} />
                      </Button>
                      {cycle.is_active && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleArchive(cycle.id)}
                          className="text-amber-400 hover:text-amber-300"
                        >
                          <Archive size={14} />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-[#111827] border-border/60 max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingCycle ? "Edit Cycle" : "Create Cycle"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Batch</Label>
                <Select
                  value={form.batch_id}
                  onValueChange={(v) =>
                    setForm((p) => ({ ...p, batch_id: v }))
                  }
                  disabled={!!editingCycle}
                >
                  <SelectTrigger className="mt-1 bg-secondary/50 border-border/60">
                    <SelectValue placeholder="Select batch" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111827] border-border/60">
                    {batches.map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cycle Name</Label>
                <Input
                  placeholder="e.g. July 2026"
                  value={form.name}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, name: e.target.value }))
                  }
                  className="mt-1 bg-secondary/50 border-border/60"
                />
              </div>
              <div>
                <Label>Template (optional)</Label>
                <Select
                  value={form.template_id}
                  onValueChange={(v) =>
                    setForm((p) => ({ ...p, template_id: v }))
                  }
                >
                  <SelectTrigger className="mt-1 bg-secondary/50 border-border/60">
                    <SelectValue placeholder="Select template (optional)" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111827] border-border/60 max-h-48">
                    <SelectItem value="__none__">None</SelectItem>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name} v{t.version}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {error && (
                <p className="text-sm text-red-400">{error}</p>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#2BB5D8] hover:bg-[#25a3c4] text-[#0D1117] font-semibold"
              >
                {saving ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : editingCycle ? (
                  "Update"
                ) : (
                  "Create"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
